<template>
  <div key="textinput" class="knw_widget_inputbar">
    <Transition name="showButton">
      <button
        v-if="showFileUpload"
        :aria-label="t('input.buttons.attachment')"
        class="userselect_inputtype_button knw_widget_inputbar_btn"
        @click="openFileUpload"
      >
        <IconPaperclip aria-hidden="true" />
      </button>
    </Transition>

    <AutosizeTextAreaVue
      ref="textinput"
      v-model="message"
      rows="1"
      :maxlength="maxCharCount"
      :placeholder="t('textinput.placeholder')"
      :max-height="maxheight"
      :class="{ bordered: showSendButton, 'no-file-padding': !showFileUpload }"
      class="knw_widget_inputbar_textarea"
      @focus="setFocus"
      @input="countChars"
      @blur="releaseFocus"
      @keydown.enter.exact.prevent
      @keyup.enter.exact="handleUserInput"
      @keydown.enter.shift.exact="newline"
    />

    <div class="knw_send_button_container">
      <span v-if="charsLeft.showCount" class="charCounter" :class="charsLeft.classes">
        {{ charsLeft.count }}
      </span>

      <!-- <transition name="fadeIn"> -->
      <button
        :aria-label="t('input.buttons.send')"
        class="knw_widget_inputbar_btn"
        :class="{ showButton: showSendButton }"
        @click="handleUserInput"
      >
        <IconSend aria-hidden="true" />
      </button>
      <!-- </transition> -->
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ChatInputCapabilities } from "@/types";
import IconPaperclip from "@/svg/IconPaperclip.vue";
import { computed, nextTick, onMounted, ref } from "vue";
import AutosizeTextAreaVue from "@/components/inputs/TextInput/AutosizeTextArea.vue";
import signals from "@/common/signals";
import IconSend from "@/svg/IconSend.vue";
import { useI18n } from "@/composables/useI18n";
const { t } = useI18n();

const emit = defineEmits(["on-text-input", "request-input-type"]);
const message = defineModel({ type: String, default: "" });
const {
  maxCharCount = 256,
  capabilities,
  hasInputFocus,
} = defineProps<{
  maxCharCount?: number;
  capabilities: ChatInputCapabilities | undefined;
  hasInputFocus: boolean;
}>();

const textinput = ref();
const maxheight = ref(40);
const focus = ref(false);
const charCount = ref(maxCharCount);

const showSendButton = computed(() => message.value !== "" || focus.value);
const focusOnInputfield = computed(() => hasInputFocus);
const showFileUpload = computed(() => capabilities?.fileupload);

const charsLeft = computed(() => {
  const left = maxCharCount - message.value.length;

  if (left > 50)
    return {
      count: left,
      classes: "",
      showCount: false,
    };
  if (!left)
    return {
      count: 0,
      classes: "nearMaxCount",
      showCount: true,
    };
  return {
    count: left,
    classes: "",
    showCount: true,
  };
});

const focusInputElement = () => {
  // iOS Hack to scroll down to the input element
  // not pretty and scrolls the window in the background
  // but currently I can't find a propper way to deal with that.
  if (window?.parent?.innerWidth >= 550 && focusOnInputfield.value) {
    textinput.value.focus();
  }
};
const newline = () => {
  message.value = `${message.value}\n`;
};
const countChars = () => {
  // we need to prevent the initial "too big textarea" because of the faulty height calc inside of iframes
  maxheight.value = 100;
  charCount.value = textinput.value.modelValue.length - maxCharCount;
};
const setFocus = () => {
  focus.value = true;
};
const releaseFocus = () => {
  focus.value = false;
};
const handleUserInput = () => {
  if (message.value.trim() !== "") {
    signals.userInput("textinput", {
      action: "user_input",
      message: message.value.trim(),
    });
    emit("on-text-input", message.value.trim());
    message.value = "";
    charCount.value = 0;
  }
};

const openFileUpload = () => {
  emit("request-input-type", { type: "fileupload", options: capabilities?.fileupload.options });
};

onMounted(() => {
  nextTick(() => {
    focusInputElement();
  });
});
</script>

<style lang="scss">
.no-file-padding {
  padding-left: 8px;
}

.knw_widget_inputbar:has(textarea:focus-within) {
  border-color: black;
}

.knw_widget_inputbar {
  position: relative;
  display: flex;
  align-items: center;
  z-index: 100;
  height: fit-content;
  //max-height: 30%;
  //margin: 3px 0;
  padding: 4px !important;
  background: var(--moin-textinput-background, white) !important;
  border-radius: 6px;
  border: 1px solid;
  border-color: var(--moin-textinput-textarea-borderColor, #e0e0e0);

  .knw_widget_inputbar_textarea {
    background: $kw-global-background-input;
    background: var(--moin-textinput-background, transparent) !important;
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
  }

  .knw_send_button_container {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    justify-items: end;
    height: 100%;
  }

  .knw_send_button_container:has(:nth-child(2)) {
    justify-content: space-between;
    /* Adjust when there are two items */
  }

  textarea {
    overflow-y: auto;
    background: $kw-global-background-white;

    resize: none;
    width: 100%;
    max-height: 85px;
    min-height: 0;
    @include default-font;
    // reset the line height
    line-height: normal;
    color: #333;
    border: none;
    transition: border 0.15s, height 0.15s;
    outline: none !important;

    &::-webkit-scrollbar {
      display: none;
    }

    &::placeholder {
      color: $kw-global-textcolor-medium;
    }
  }

  .knw_widget_inputbar_btn {
    align-self: flex-end;
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 2px;
    background: transparent;
    border: none;
    padding: 0 0 0 0;
    font-size: 1.6rem;
    opacity: var(--moin-textinput-sendbutton-opacity, 0.75);
    transition: all $kw-default-transition-speed-fast ease-out;
    transform: scale(1);
    @include default-focus;

    /*    transform: translateX(10px);*/
    &.showButton {
      opacity: 1;
      /*      transform: translateX(0px);*/
    }

    &:hover,
    &:focus {
      cursor: pointer;
      opacity: 1;
      background-color: #ededed;
    }
  }
}

/** TODO: Are these used anymore? */
.knw_widget_inputbar_btn {
  #Sendbutton {
    fill: var(--moin-textinput-sendbutton-circle, #a9a9a9);
  }

  #Paperplane {
    fill: var(--moin-textinput-sendbutton-plane, #ffffff);
  }
}

.charCounter {
  text-align: center;
  font-size: 1.4rem;
  padding: 0 5px;
  color: #6d6d6d;
  //color: $kw-global-textcolor-medium;
  transition: color 0.4s ease;

  &.nearMaxCount {
    // color: $kw-global-textcolor-medium;
    color: darkred;
  }
}

.userselect_inputtype_button {
  /** TODO Should we just inherit from send button instead of defining new styles for attachment button? */
  background: none;
  border: none;
  height: 40px;
  width: 40px;
  padding: 0;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: flex-end;
  transform: scale(1);

  /** TODO: Inherit color and style from the main button, but do we still need to keep this customizable via separate var? */
  /*
  svg {
    fill: var(--moin-textinput-sendbutton-circle, #a9a9a9);
  }
   */
}

/* we will explain what these classes do next! */
.showButton-enter-active,
.showButton-leave-active {
  transition: all 0.5s ease;
}

.showButton-enter-from,
.showButton-leave-to {
  opacity: 0;
  width: 0px;
  transform: scale(0.5);
}
</style>
